import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EService } from '@datatype/type';
import { IMoashelfMenu } from '@datatype/moashelf/type';
import { IIyagigoMenu, TIyagigoMainMenu, TIyagigoSubMenu } from '@datatype/iyagigo/type';

interface IState {
  /** 로그인 하기 전 페이지 **/
  beforeSigninPage: string;

  isMobileSideTab: boolean;
  /** side menu 설정 **/
  moashelf: {
    mainMenu: IMoashelfMenu['main'];
    subMenu: IMoashelfMenu['sub'];
  };
  iyagigo: {
    mainMenu: IIyagigoMenu['main'];
    subMenu: IIyagigoMenu['sub'];
  };
}

const initialState: IState = {
  beforeSigninPage: '/',

  isMobileSideTab: false,
  // moashelf
  moashelf: {
    mainMenu: 'begin',
    subMenu: 'begin-today',
  },
  // iyagigo
  iyagigo: {
    mainMenu: null,
    subMenu: null,
  },
};

// iyagigo 메인메뉴별 기본 서브메뉴
export const DEFAULT_IYAGIGO_SUB_MENU: Record<Exclude<TIyagigoMainMenu, null>, IIyagigoMenu['sub']> = {
  home: 'begin',
  cloze: 'vocabulary',
  classroom: 'character',
  clip: 'authentic-expression',
  speaking: 'talk',
  test: 'problem',
  studio: 'statistic',
  hub: null,
  begin: null,
  'vocabulary-book': 'vocabulary-book-study',
};

const initializeSubMenu = (state) => {
  state.moashelf = {
    mainMenu: null,
    subMenu: null,
  };

  state.iyagigo = {
    mainMenu: null,
    subMenu: null,
  };
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    initialize: (state) => {
      for (const key in state) {
        state[key] = initialState[key];
      }
    },

    storeBeforeSigninPage: (state, action: PayloadAction<string>) => {
      state.beforeSigninPage = action.payload;
    },
    removeBeforeSigninPage: (state) => {
      state.beforeSigninPage = '/';
    },

    /**
     * @description 브라우저의 URL의 /ms/:page /ig/:page를 읽어 navigation main menu와 동기화
     */
    syncPageAndMenu: (state, action: PayloadAction<{ service: EService; page: string }>) => {
      const { service, page } = action.payload;

      //************************* moashelf *************************//
      if (service === EService.MOASHELF && page !== state.moashelf.mainMenu) {
        state.moashelf.mainMenu = page as IMoashelfMenu['main'];
        initializeSubMenu(state);
        if (page === 'shelf') state.moashelf.subMenu = 'shelf-all';
        if (page === 'subscription') state.moashelf.subMenu = 'subscription-all';
        if (page === 'begin') state.moashelf.subMenu = 'begin-today';
      }

      //************************* iyagigo *************************//
      // console.log('page', page);
      // if (service === 'iyagigo' && page !== state.iyagigo.mainMenu) {
      //   state.iyagigo.mainMenu = page as IIyagigoMenu['main'];
      //   initializeSubMenu(state);
      //   if (page === 'test') state.iyagigo.subMenu = INITIAL_TEST_SUB_MENU;
      //   // if (page === 'setting') state.iyagigo.subMenu = INITIAL_SETTING_SUB_MENU;
      // }
    },

    // ###################################
    // iyagigo main menu click
    // - 메인메뉴가 바뀔 때만 subMenu를 기본값으로 설정
    // - 같은 메인메뉴 재호출 시엔 subMenu 보존
    // ###################################
    storeIyagigoMainMenu: (state, action: PayloadAction<IIyagigoMenu['main']>) => {
      if (state.iyagigo.mainMenu !== action.payload) {
        state.iyagigo.subMenu = action.payload ? DEFAULT_IYAGIGO_SUB_MENU[action.payload] : null;
      }
      state.iyagigo.mainMenu = action.payload;
    },

    // ###################################
    // iyagigo sub menu click
    // ###################################
    // TODO: route 기능까지 넣기...
    storeIyagigoSubMenu: (
      state,
      action: PayloadAction<{ mainMenu: TIyagigoMainMenu; subMenu: TIyagigoSubMenu[keyof TIyagigoSubMenu] }>,
    ) => {
      initializeSubMenu(state);

      const { mainMenu, subMenu } = action.payload;

      // subMenu가 null/undefined로 들어오면 해당 mainMenu의 기본 subMenu로 fallback
      const resolvedSubMenu = subMenu ?? (mainMenu ? DEFAULT_IYAGIGO_SUB_MENU[mainMenu] : null);

      switch (mainMenu) {
        case 'hub':
        case 'home':
        case 'cloze':
        case 'classroom':
        case 'clip':
        case 'speaking':
        case 'test':
        case 'studio':
        case 'vocabulary-book':
          state.iyagigo.mainMenu = mainMenu;
          state.iyagigo.subMenu = resolvedSubMenu;
          break;
        default:
          throw new Error(`invaild main menu - ${mainMenu}`);
      }
    },

    // ###################################
    // moashelf main menu click
    // ###################################
    storeMoashelfMainMenu: (state, action: PayloadAction<IMoashelfMenu['main']>) => {
      initializeSubMenu(state);
      state.moashelf.mainMenu = action.payload;
    },

    // ###################################
    // moashelf sub menu click
    // ###################################
    storeMoashelfSubMenu: (state, action: PayloadAction<IMoashelfMenu['sub']>) => {
      initializeSubMenu(state);
      const subMenu = action.payload;

      if (['begin-today', 'begin-find', 'begin-tip'].includes(subMenu!)) {
        state.moashelf.mainMenu = 'begin';
        state.moashelf.subMenu = subMenu;
      } else if (['shelf-all', 'shelf-archive', 'shelf-bookmark', 'shelf-trash', 'shelf-unread'].includes(subMenu!)) {
        state.moashelf.mainMenu = 'shelf';
        state.moashelf.subMenu = subMenu;
      } else if (['subscription-all', 'subscription-bookmark', 'subscription-trash'].includes(subMenu!)) {
        state.moashelf.mainMenu = 'subscription';
        state.moashelf.subMenu = subMenu;
      } else {
      }

      return;
    },

    openMobileSideTab: (state) => {
      state.isMobileSideTab = true;
    },

    closeMobileSideTab: (state) => {
      state.isMobileSideTab = false;
    },
    // clickBottomTabMenu: (state, action: PayloadAction<IState['bottomTabMenu']>) => {
    //   state.subMenu = action.payload;
    // },
  },
});

export const actions = navigationSlice.actions;
export default navigationSlice.reducer;
